import api from "../api";
import type { IntegrationDetail, ScheduleItem } from "./integrationsBeta";
import { fetchAllPages } from "./paginatedList";

const schedTag = (integrationId: number) => ({
  type: "IntegrationSchedule" as const,
  id: integrationId,
});

const monitorInvalidation = (integrationId: number) => [
  schedTag(integrationId),
  { type: "IntegrationMonitor" as const, id: integrationId },
  { type: "IntegrationMonitor" as const, id: "LIST" as const },
];

export const schedulesBetaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSchedules: builder.query<ScheduleItem[], number>({
      // Keeps the ScheduleItem[] cache shape that the optimistic updates of
      // toggleSchedule / toggleIntegration patch in place.
      queryFn: (integrationId, _api, _extra, fetchWithBQ) =>
        fetchAllPages<ScheduleItem>(
          fetchWithBQ,
          "/integration-platform/schedules/",
          { integration: integrationId },
        ),
      providesTags: (_result, _err, integrationId) => [schedTag(integrationId)],
    }),

    createSchedule: builder.mutation<
      ScheduleItem,
      { integration: number; display_name: string; cron_expression: string }
    >({
      query: (body) => ({
        url: "/integration-platform/schedules/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { integration }) =>
        monitorInvalidation(integration),
    }),

    updateSchedule: builder.mutation<
      ScheduleItem,
      {
        id: number;
        integration: number;
        display_name?: string;
        cron_expression?: string;
      }
    >({
      query: ({ id, integration, ...body }) => ({
        url: `/integration-platform/schedules/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { integration }) =>
        monitorInvalidation(integration),
    }),

    deleteSchedule: builder.mutation<
      unknown,
      { id: number; integration: number }
    >({
      query: ({ id }) => ({
        url: `/integration-platform/schedules/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { integration }) =>
        monitorInvalidation(integration),
    }),

    toggleSchedule: builder.mutation<
      ScheduleItem,
      { id: number; integration: number; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/integration-platform/schedules/${id}/toggle/`,
        method: "PATCH",
        body: { is_active },
      }),
      // Optimistic: flip this schedule immediately and reflect the backend's
      // integration cascade (enable → integration on; disable last → off).
      async onQueryStarted(
        { id, integration, is_active },
        { dispatch, queryFulfilled },
      ) {
        // Cast: endpoints are injected across files (see integrationsBeta.ts).
        const upd = api.util.updateQueryData as unknown as <T>(
          endpoint: string,
          arg: unknown,
          updater: (draft: T) => void,
        ) => unknown;
        const patches: { undo: () => void }[] = [
          dispatch(
            upd<ScheduleItem[]>("getSchedules", integration, (draft) => {
              const s = draft.find((x) => x.id === id);
              if (s) s.is_active = is_active;
            }) as never,
          ) as { undo: () => void },
          dispatch(
            upd<IntegrationDetail>(
              "getIntegrationDetail",
              integration,
              (draft) => {
                const s = draft.schedules.find((x) => x.id === id);
                if (s) s.is_active = is_active;
                if (is_active) {
                  draft.is_active = true;
                } else if (!draft.schedules.some((x) => x.is_active)) {
                  draft.is_active = false;
                }
              },
            ) as never,
          ) as { undo: () => void },
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (_r, _e, { integration }) =>
        monitorInvalidation(integration),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSchedulesQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useToggleScheduleMutation,
} = schedulesBetaApi;
